/**
 * SSE client for streaming endpoints (currently POST /v1/chat/messages).
 *
 * Mirrors the canonical integration-test helper
 * `tests/integration/helpers/sseClient.ts` in `towninsider-api` — same
 * wire-protocol parser, same content-type branch. Treat the integration
 * test as the source of truth for the contract.
 *
 *   text/event-stream  → yield SseEvent values from the generator
 *   application/json   → parse the uniform error envelope, throw ApiError
 *
 * Auth: optional `getToken` callback (matches `apiFetch`). When provided
 * and the resolved token is non-null, sent as Bearer. When absent or
 * null, the request fires without an Authorization header — the server
 * returns 401 for authed endpoints, which surfaces here as a pre-stream
 * `ApiError` with code `'unauthorized'`.
 *
 * Aborts: pass `signal: AbortController.signal`. The underlying fetch is
 * aborted at the network layer; the generator's `finally` block releases
 * the reader lock for clean teardown on route changes / unmount. Aborts
 * surface as `ApiError` with code `'aborted'` — callers should suppress
 * error UI for this code.
 *
 * Mid-stream `error` events arrive as ordinary `SseEvent` frames with
 * `event === 'error'`. They are NOT thrown — the stream closes cleanly
 * after the error frame and the caller distinguishes by event name.
 * Only PRE-stream failures (auth, validation, rate limit, network)
 * throw from this generator.
 *
 * Connection heartbeat (Batch 1c, 11 Jun 2026): every network-layer
 * outcome reports into `lib/connection.ts` so the outbox can gate
 * draining on real ground truth (caravan-park wifi often reports
 * `navigator.onLine = true` while every API call fails — the
 * heartbeat catches that). Classification:
 *
 *   reportApiSuccess on:
 *     - 2xx response (with or without an SSE stream)
 *     - 3xx response
 *     - 4xx response (network reached the server; the rejection is
 *       app-level — auth, validation, rate-limit — not connectivity)
 *
 *   reportApiFailure on:
 *     - Network error before the response (fetch threw, not abort)
 *     - 5xx response (server fault, back off)
 *     - 200 response with body===null (server bug, treat as fault)
 *     - Mid-stream non-abort error (reader.read() rejected after the
 *       stream opened — caravan-wifi mid-flight drop)
 *
 *   No report on:
 *     - Caller-initiated abort (route change, unmount) — abort is the
 *       caller's choice, not a network signal.
 *     - SSE `event: error` frames mid-stream — the server reached us
 *       fine, the error is logical (persona_overloaded, etc), not
 *       connectivity. The yielded event surfaces to the caller, who
 *       decides whether to mark the outbox item failed-retryable or
 *       failed-permanent.
 */
import { ApiError, getApiBaseUrl } from '../api';
import { reportApiFailure, reportApiSuccess } from '../connection';
export interface SseEvent {
  /** SSE `event:` field — e.g. 'text', 'complete', 'error'. */
  event: string;
  /**
   * SSE `data:` field, parsed as JSON. Falls back to the raw string on
   * parse failure (server contract guarantees JSON, fallback keeps
   * diagnostics readable instead of silently dropping the frame).
   */
  data: unknown;
}
export interface ApiStreamOptions {
  method: 'POST' | 'GET';
  body?: unknown;
  signal?: AbortSignal;
  headers?: HeadersInit;
  /**
   * Optional auth-token getter (matches `apiFetch`). When provided and
   * the resolved value is non-null, sent as Bearer. When absent or
   * null, no Authorization header is sent.
   */
  getToken?: () => Promise<string | null>;
}
/**
 * Open an SSE stream and yield events as they arrive.
 *
 * The fetch is initiated on the first `.next()` call (i.e. when the
 * caller begins iterating). Pre-stream errors throw before the first
 * yield, so a `try / for await` pattern catches them naturally:
 *
 *   try {
 *     for await (const event of apiStream(path, opts)) {
 *       handle(event);
 *     }
 *   } catch (err) {
 *     if (err instanceof ApiError && err.code === 'aborted') return;
 *     // surface persona-voiced soft error
 *   }
 *
 * Throws `ApiError` on:
 *   - Pre-stream JSON error envelope (status 4xx/5xx, content-type
 *     application/json) — `code` from the envelope or status-mapped.
 *   - Network failure before headers received — `code: 'network_error'`.
 *   - Caller-initiated abort — `code: 'aborted'`.
 *   - Missing response body on 200 — `code: 'server_error'`.
 */
export async function* apiStream(
  path: string,
  options: ApiStreamOptions,
): AsyncGenerator<SseEvent> {
  const { method, body, signal, headers, getToken } = options;
  const token = getToken ? await getToken() : null;
  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'text/event-stream');
  if (body !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (token) {
    finalHeaders.set('Authorization', `Bearer ${token}`);
  }
  // Build fetch init with conditional spread to satisfy
  // exactOptionalPropertyTypes — passing `body: undefined` or
  // `signal: undefined` is forbidden when the field is declared optional
  // without explicit `| undefined`.
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      method,
      headers: finalHeaders,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal !== undefined ? { signal } : {}),
    });
  } catch (err) {
    // Abort surfaces as DOMException name='AbortError' on every modern
    // engine. Distinguish so callers can suppress error UI on
    // intentional aborts (route change, component unmount).
    if (err instanceof DOMException && err.name === 'AbortError') {
      // No heartbeat report — abort is the caller's choice, not a
      // signal about network health.
      throw new ApiError(0, 'aborted', 'Request aborted', err);
    }
    // Genuine network failure — fetch couldn't reach the server.
    // Report to the connection module so the outbox knows to back
    // off proactive sends until connectivity recovers.
    reportApiFailure();
    throw new ApiError(0, 'network_error', 'Network request failed', err);
  }
  // Got an HTTP response — classify by status for the heartbeat.
  // 5xx = server fault (report failure, back off); 2xx-4xx = network
  // reached the server (report success, the rejection is app-level
  // and not a connectivity signal). The body-null-on-2xx case below
  // overrides success with failure for that specific server bug.
  if (response.status >= 500) {
    reportApiFailure();
  } else {
    reportApiSuccess();
  }
  const contentType = response.headers.get('content-type') ?? '';
  // ── Pre-stream error branch ────────────────────────────────────────
  // Server flips Content-Type to application/json for any failure
  // before the stream opens (auth, validation, rate limit, persona
  // misconfig). Drain the body, surface as ApiError so the caller
  // path is the same as for `apiFetch`. Heartbeat already reported
  // above by the status-based classification.
  if (!contentType.includes('text/event-stream')) {
    let payload: unknown = null;
    const text = await response.text();
    if (text.length > 0) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }
    const errorEnvelope =
      payload && typeof payload === 'object' && 'error' in payload
        ? (payload as {
            error?: { code?: string; message?: string; details?: unknown };
          }).error
        : null;
    const code = errorEnvelope?.code ?? mapStatusToCode(response.status);
    const message =
      errorEnvelope?.message ?? response.statusText ?? 'Request failed';
    throw new ApiError(
      response.status,
      code,
      message,
      errorEnvelope?.details,
    );
  }
  if (response.body === null) {
    // 200 with no body — server bug. The status-based heartbeat
    // above reported success (status < 500); override that to
    // failure since the response is unusable.
    reportApiFailure();
    throw new ApiError(
      response.status,
      'server_error',
      'Empty SSE response body',
    );
  }
  // ── Stream branch ──────────────────────────────────────────────────
  // Frames are separated by `\n\n`. Within a frame, lines starting with
  // `event:` or `data:` carry the payload. Anthropic's stream protocol
  // is bigger than this; the chat endpoint deliberately emits only
  // `event` + `data` pairs (no `id`, no `retry`).
  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .getReader();
  let buffer = '';
  // Track whether the stream loop exited via error vs clean drain.
  // Set inside the catch so the finally can distinguish abort (no
  // heartbeat report) from genuine mid-stream drop (report failure).
  // The status-based reportApiSuccess above stands when the stream
  // drains cleanly or the consumer breaks early after receiving a
  // complete / error frame — those aren't connectivity issues.
  let streamError: unknown = null;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseFrame(frame);
        if (parsed !== null) yield parsed;
      }
    }
    // Drain any trailing frame without a closing blank line. The server
    // always emits `\n\n` after each event so this is defensive only;
    // present to keep parser robust against partial-flush edge cases.
    if (buffer.length > 0) {
      const parsed = parseFrame(buffer);
      if (parsed !== null) yield parsed;
    }
  } catch (err) {
    streamError = err;
    throw err;
  } finally {
    // Release the reader lock so the underlying stream can be cancelled
    // when the consumer aborts mid-iteration. Required for clean
    // teardown on unmount or route change — without this, the stream
    // is held open until garbage collection.
    reader.releaseLock();
    // Mid-stream error reporting. Abort gets no report (caller's
    // choice). Genuine mid-stream drop (network dropped mid-stream,
    // pipe error, etc) reports failure — overrides the optimistic
    // reportApiSuccess we fired at the status check.
    if (
      streamError !== null &&
      !(streamError instanceof DOMException && streamError.name === 'AbortError')
    ) {
      reportApiFailure();
    }
  }
}
function parseFrame(frame: string): SseEvent | null {
  let event = '';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event: ')) {
      event = line.slice('event: '.length).trim();
    } else if (line.startsWith('data: ')) {
      dataLines.push(line.slice('data: '.length));
    }
    // Comment lines (`:`) and other SSE field names (`id`, `retry`)
    // are ignored — the chat endpoint only emits `event` + `data`.
  }
  if (event === '') return null;
  const dataStr = dataLines.join('\n');
  let data: unknown;
  if (dataStr === '') {
    data = null;
  } else {
    try {
      data = JSON.parse(dataStr);
    } catch {
      // Server contract: `data:` is always JSON. Falling back to the
      // raw string keeps the test failure / debug surface diagnostic
      // rather than silently dropping the frame.
      data = dataStr;
    }
  }
  return { event, data };
}
function mapStatusToCode(status: number): string {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown_error';
}
